import { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { Traveller } from "@/app/common-types/quote/question-payload-type";
import { QUOTE_JOURNEY_TYPE } from "@/config/app-constant";
import axioInstance from "@/libs/axios";
import { RootState } from "@/store/root-reducer";
import { QuoteData } from "@/types/quote/quote-data";
import logError from "@/utils/logger";

export default function useSavePMIDetails(
  resetTrigger: () => void,
  isTravelerEditOrOrganizerEdit: boolean,
  medicalEdit: boolean
) {
  const [data, setData] = useState<null | QuoteData>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | AxiosError>(null);
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const { attributes, version, referenceId } = quoteResponse;

  const savePMIDetails = useCallback(
    async (formData: Traveller[]) => {
      // Check if we're in any edit mode
      const isEditMode = isTravelerEditOrOrganizerEdit || medicalEdit;

      if (isEditMode) {
        // Edit mode: Skip API, but still update Redux and navigate
        const updatedQuoteData: QuoteData = {
          attributes: {
            ...attributes,
            travellerDetails: {
              ...attributes.travellerDetails,
              traveller: formData,
            },
          },
          version,
          referenceId,
          type: QUOTE_JOURNEY_TYPE,
        };

        // Set the data directly (simulate API response)
        setData(updatedQuoteData);
        setLoading(false);
        resetTrigger();
        return;
      }

      // Normal mode: Make actual API call
      const body = {
        data: {
          type: QUOTE_JOURNEY_TYPE,
          channel: "ONLINE",
          version,
          viewPage: "PMI",
          attributes: {
            ...attributes,
            travellerDetails: {
              ...attributes.travellerDetails,
              traveller: formData,
            },
          },
        },
      };

      try {
        setLoading(true);
        const response = await axioInstance.patch(
          `${process.env.NEXT_PUBLIC_BASE_URL}/quote/${referenceId}/initialQuestionSet`,
          body
        );
        setData(response.data.data);
      } catch (err) {
        const axiosError = err as AxiosError;
        logError(axiosError, { location: "PMI Page Save" });
        setError(axiosError);
      } finally {
        setLoading(false);
        resetTrigger();
      }
    },
    [
      isTravelerEditOrOrganizerEdit,
      medicalEdit,
      version,
      attributes,
      referenceId,
      resetTrigger,
    ]
  );

  return { data, loading, error, savePMIDetails };
}
